import { Action } from 'redux'
import { namespace } from '.'
import { combineEpics, Epic, ofType } from 'redux-observable'
import { filter, map, merge, mergeMap, of, take, takeUntil, timer } from 'rxjs'
import { createInvokeEpic } from '../../../signalr/createInvokeEpic'
import { createStreamEpic } from '../../../signalr/createStreamEpic'
import { addToast } from '../../toasts/actions'
import {
  type SystemParamSaveAction,
  type SystemParamsDeleteAction,
  systemParamChangeConfirmed,
  systemParamChangeTimedOut
} from './actions'
import {
  fetchSystemParamDefinition,
  saveSystemParamDefinition,
  deleteSystemParamsDefinition
} from './definition'
import type { State } from './reducer'
import { getSystemParams } from './selectors'
import type { SystemParam } from './types'

export const changeTimeoutMs = 10000

type ChangeAction = SystemParamSaveAction | SystemParamsDeleteAction

const isDelete = (action: ChangeAction): action is SystemParamsDeleteAction =>
  action.type === 'sysparam.deleteSystemParams'

/*
  SaveSystemParam and DeleteSystemParams both return success the moment the
  request is queued, so the only proof a change took effect is the parameter
  coming back down the stream. A new one has no id yet, so it is matched on
  name; an edit is matched on the values we sent, because the row already
  carries the old ones.
*/
const hasArrived = (action: ChangeAction) => (params: SystemParam[]) => {
  if (isDelete(action)) {
    return action.payload.every(
      ({ id }) => !params.some((param) => param.id === id)
    )
  }
  const saved = action.payload
  if (saved.id === 0) {
    return params.some(
      (param) => param.name.toLowerCase() === saved.name.toLowerCase()
    )
  }
  return params.some(
    (param) =>
      param.id === saved.id &&
      param.name === saved.name &&
      param.description === saved.description &&
      param.value === saved.value
  )
}

const timedOutToast = (action: ChangeAction) => {
  const names = isDelete(action)
    ? action.payload.map(({ name }) => name).join(', ')
    : action.payload.name
  const verb = isDelete(action) ? 'deleting' : 'saving'
  return addToast({
    message: `The server hasn't confirmed ${verb} ${names}.`,
    options: { toastId: `sysparam-timeout-${names}`, type: 'warning' }
  })
}

const awaitChangeEpic: Epic<Action, Action, { [namespace]: State }> = (
  action$,
  state$
) =>
  action$.pipe(
    ofType('sysparam.saveSystemParam', 'sysparam.deleteSystemParams'),
    mergeMap((action: ChangeAction) => {
      const arrived = hasArrived(action)
      /*
        takeUntil rather than race, because race drops the timer as soon as
        the action stream ends, and in a test that stream is finite.
      */
      const confirmed$ = action$.pipe(
        ofType('sysparam.addSystemParams'),
        filter(() => arrived(getSystemParams(state$.value) ?? [])),
        take(1)
      )
      return merge(
        confirmed$.pipe(map(() => systemParamChangeConfirmed())),
        timer(changeTimeoutMs).pipe(
          takeUntil(confirmed$),
          mergeMap(() => of(systemParamChangeTimedOut(), timedOutToast(action)))
        )
      ).pipe(
        takeUntil(
          action$.pipe(
            ofType(
              'sysparam.saveSystemParamFailure',
              'sysparam.deleteSystemParamsFailure'
            )
          )
        )
      )
    })
  )

export default combineEpics(
  createStreamEpic('sysparam.fetch', fetchSystemParamDefinition),
  createInvokeEpic('sysparam.saveSystemParam', saveSystemParamDefinition),
  createInvokeEpic('sysparam.deleteSystemParams', deleteSystemParamsDefinition),
  awaitChangeEpic
)
